import { SITE_URL, getAbsoluteSiteUrl } from '@/data/blogSeoMetadata';

export { SITE_URL, getAbsoluteSiteUrl };

export const COMPANION_APP_PATH = '/companion-app';
export const COMPANION_APP_CANONICAL_URL = getAbsoluteSiteUrl(COMPANION_APP_PATH);

export const COMPANION_APP_NAME = 'CalorieScience Companion';

export const COMPANION_APP_PAGE_TITLE = 'CalorieScience Companion | Personalized Nutrition App';

export const COMPANION_APP_META_DESCRIPTION =
  "Access personalized meal plans, upload lab reports and track nutrition, symptoms and progress with your dietitian, nutritionist, health coach or fitness professional.";

/**
 * Live App Store listing used by download CTAs and structured data.
 */
export const COMPANION_APP_STORE_URL: string | null =
  'https://apps.apple.com/in/app/caloriescience-companion/id6797737280';

/**
 * Numeric App Store ID (the digits in an apps.apple.com/app/id<NUMBER> URL).
 * Required for the Apple Smart App Banner meta tag.
 */
export const COMPANION_APP_STORE_ID: string | null = '6797737280';

/**
 * Set once the listing is live on Google Play. Leaving this null keeps the
 * download CTA from shipping a fabricated or broken link.
 */
export const COMPANION_PLAY_STORE_URL: string | null = null;

export const COMPANION_APP_ICON_PATH = '/images/companion/icon-1024.png';
export const COMPANION_APP_BANNER_PATH = '/images/companion/banner.png';

/** Absolute URLs, for meta tags and structured data only — use the *_PATH constants for on-page <img> tags. */
export const COMPANION_APP_ICON_URL = getAbsoluteSiteUrl(COMPANION_APP_ICON_PATH);
export const COMPANION_APP_BANNER_URL = getAbsoluteSiteUrl(COMPANION_APP_BANNER_PATH);

export const COMPANION_APP_SCREENSHOTS = [
  {
    src: '/images/companion/screenshot-meal-plan.png',
    alt: "CalorieScience Companion home screen showing today's meal plan and weight-goal progress",
  },
  {
    src: '/images/companion/screenshot-sessions.png',
    alt: 'CalorieScience Companion sessions screen for booking and managing appointments with a nutrition professional',
  },
  {
    src: '/images/companion/screenshot-trends.png',
    alt: 'CalorieScience Companion trends screen showing weight progress and lab biomarkers over time',
  },
  {
    src: '/images/companion/screenshot-meal-details.png',
    alt: 'CalorieScience Companion meal detail screen with a nutrition breakdown and ingredient list',
  },
  {
    src: '/images/companion/screenshot-invoices.png',
    alt: 'CalorieScience Companion invoice screen for reviewing nutrition program billing',
  },
] as const;

export const COMPANION_APP_ABSOLUTE_SCREENSHOTS = COMPANION_APP_SCREENSHOTS.map(shot => ({
  ...shot,
  url: getAbsoluteSiteUrl(shot.src),
}));

export const COMPANION_APP_COMPLIANCE_DISCLOSURE =
  'CalorieScience Companion is a workflow and tracking tool that supports the guidance of your dietitian, nutritionist, health coach or fitness professional. It is UK and EU GDPR compliant; HIPAA is in progress. It is not a diagnostic tool, not a medical device, and does not replace clinical assessment by a qualified practitioner.';

export type CompanionFaq = {
  question: string;
  answer: string;
};

export const COMPANION_APP_FAQS: CompanionFaq[] = [
  {
    question: 'What is CalorieScience Companion?',
    answer:
      "CalorieScience Companion is the client app for CalorieScience. It connects you with your dietitian, nutritionist, health coach or fitness professional so you can access your meal plan, track your food, macros, symptoms and progress, and upload lab reports, all in one place on your phone.",
  },
  {
    question: 'Who can use the Companion app?',
    answer:
      'Anyone working with a nutrition, health or fitness professional who uses CalorieScience. Your practitioner sends you an invitation to connect your account before you can access your plan and data.',
  },
  {
    question: 'Can I access meal plans from my nutritionist?',
    answer:
      "Yes. Once you're connected to your nutritionist, their meal plans appear in the app and update automatically whenever your plan changes.",
  },
  {
    question: 'Can health and fitness coaches use CalorieScience?',
    answer:
      'Yes. CalorieScience Companion works with dietitians, nutritionists, health coaches and fitness professionals who manage their clients on the CalorieScience platform.',
  },
  {
    question: 'Can I upload lab reports?',
    answer:
      'Yes. You can upload lab reports directly in the app so your practitioner can review your biomarkers and factor them into your plan.',
  },
  {
    question: 'Does the app diagnose health conditions?',
    answer:
      'No. CalorieScience Companion does not diagnose, treat or manage any medical condition, and it is not a substitute for professional medical advice. It supports the guidance already given to you by your dietitian, nutritionist, health coach or fitness professional.',
  },
  {
    question: 'How is my health information protected?',
    answer:
      "Your health information is encrypted and access-controlled. CalorieScience is built to UK and EU GDPR standards, and only you and the professional you're connected to can see your data.",
  },
];
